import { getLatestRequestContext } from '@/lib/api/request-context';
import { userFacingMessageCatalog } from '@/lib/copy/user-facing-messages';
import { isTransientFetchError } from '@/lib/errors/error-utils';
import { getUserFacingErrorCopy } from '@/lib/errors/sanitize-error';
import {
  canonicalErrorRegistry,
  type CanonicalErrorDefinition,
  type CanonicalErrorCategory,
  resolveCanonicalErrorDefinition,
} from './error-taxonomy';

export type FrontendErrorKind =
  | 'validation'
  | 'network'
  | 'permission'
  | 'dependency'
  | 'unexpected';

export interface FrontendErrorMetadata {
  code?: string;
  details?: unknown;
  digest?: string;
  method?: string;
  ref?: string;
  requestId?: string;
  retryAfter?: string;
  status?: number;
  url?: string;
}

export interface ErrorStateContent {
  authRequired: boolean;
  canRetry: boolean;
  description: string;
  kind: FrontendErrorKind;
  nextAction: string;
  requestId?: string;
  retryLabel: string;
  supportRef?: string;
  title: string;
}

type ErrorRecord = Record<string, unknown>;

function asErrorRecord(value: unknown): ErrorRecord | null {
  return typeof value === 'object' && value !== null
    ? (value as ErrorRecord)
    : null;
}

function getStringValue(
  record: ErrorRecord | null,
  keys: string[]
): string | undefined {
  if (!record) {
    return undefined;
  }

  for (const key of keys) {
    const value = record[key];
    if (typeof value === 'string' && value.trim().length > 0) {
      return value.trim();
    }
  }

  return undefined;
}

function getNumberValue(
  record: ErrorRecord | null,
  keys: string[]
): number | undefined {
  if (!record) {
    return undefined;
  }

  for (const key of keys) {
    const value = record[key];
    if (typeof value === 'number' && Number.isFinite(value)) {
      return value;
    }
  }

  return undefined;
}

function getErrorMessage(error: unknown): string {
  if (error instanceof Error) {
    return error.message;
  }

  const record = asErrorRecord(error);
  return (
    getStringValue(record, ['message', 'error', 'detail', 'details']) || ''
  );
}

function extractResponseData(error: unknown): ErrorRecord | null {
  const record = asErrorRecord(error);
  const response = asErrorRecord(record?.response);
  return asErrorRecord(response?.data);
}

export function extractErrorMetadata(error: unknown): FrontendErrorMetadata {
  const record = asErrorRecord(error);
  const response = asErrorRecord(record?.response);
  const responseData = extractResponseData(error);

  return {
    code:
      getStringValue(record, ['code', 'errorCode']) ||
      getStringValue(responseData, ['code']),
    details: record?.details ?? responseData?.details,
    digest: getStringValue(record, ['digest']),
    method:
      getStringValue(record, ['method']) ||
      getStringValue(responseData, ['method']),
    ref:
      getStringValue(record, ['ref', 'referenceId', 'reference_id']) ||
      getStringValue(responseData, ['ref', 'referenceId', 'reference_id']),
    requestId:
      getStringValue(record, ['requestId', 'request_id']) ||
      getStringValue(responseData, ['requestId', 'request_id']),
    retryAfter:
      getStringValue(record, ['retryAfter', 'retry_after']) ||
      getStringValue(responseData, ['retryAfter', 'retry_after']),
    status:
      getNumberValue(record, ['status', 'statusCode']) ||
      getNumberValue(response, ['status']) ||
      getNumberValue(responseData, ['status']),
    url:
      getStringValue(record, ['url']) ||
      getStringValue(responseData, ['url']) ||
      getStringValue(response, ['url']),
  };
}

export function extractResponseMetadata(
  response: Response,
  body: unknown,
  request?: {
    method?: string;
    url?: string;
  }
): FrontendErrorMetadata {
  const record = asErrorRecord(body);

  return {
    code: getStringValue(record, ['code']),
    details: record?.details,
    method: request?.method,
    ref: getStringValue(record, ['ref', 'referenceId', 'reference_id']),
    requestId:
      getStringValue(record, ['requestId', 'request_id']) ||
      response.headers.get('x-request-id') ||
      response.headers.get('request-id') ||
      response.headers.get('x-vercel-id') ||
      undefined,
    retryAfter:
      response.headers.get('retry-after') ||
      getStringValue(record, ['retryAfter', 'retry_after']),
    status: response.status,
    url: request?.url || response.url || undefined,
  };
}

function resolveSupportRef(
  metadata: FrontendErrorMetadata,
  latestContext: ReturnType<typeof getLatestRequestContext>
): string | undefined {
  return (
    metadata.ref ||
    metadata.digest ||
    latestContext?.ref ||
    latestContext?.digest
  );
}

function isValidationMessage(message: string): boolean {
  if (isPermissionMessage(message)) {
    return false;
  }

  return /\b(required|invalid|must be|too short|too long|select|choose|enter|validation)\b/i.test(
    message
  );
}

function isPermissionMessage(message: string): boolean {
  return /\b(auth|sign in|sign-in|session expired|unauthorized|forbidden|permission|access denied|not authorized)\b/i.test(
    message
  );
}

function isDependencyMessage(message: string): boolean {
  return /\b(service unavailable|bad gateway|upstream|retry later|rate limit|too many requests)\b/i.test(
    message
  );
}

function isNetworkMessage(message: string): boolean {
  return /\b(network|offline|failed to fetch|fetch failed|load failed|connection|timed out|timeout)\b/i.test(
    message
  );
}

function resolveCanonicalDefinition(
  metadata: FrontendErrorMetadata,
  message: string
): CanonicalErrorDefinition | null {
  const canonicalMatch = resolveCanonicalErrorDefinition({
    code: metadata.code,
    status: metadata.status,
  });
  if (canonicalMatch) {
    return canonicalMatch[1];
  }

  if (isPermissionMessage(message)) {
    return canonicalErrorRegistry.authorizationForbidden;
  }

  if (isDependencyMessage(message)) {
    return canonicalErrorRegistry.dependencyServiceUnavailable;
  }

  if (
    /\b(billing|subscription|payment method|card declined|payment failed)\b/i.test(
      message
    )
  ) {
    return canonicalErrorRegistry.billingFailed;
  }

  if (/\bnot found|no longer exists|missing\b/i.test(message)) {
    return canonicalErrorRegistry.notFoundResource;
  }

  if (
    /\b(already exists|already in progress|conflict|changed since)\b/i.test(
      message
    )
  ) {
    return canonicalErrorRegistry.stateConflict;
  }

  if (isValidationMessage(message)) {
    return canonicalErrorRegistry.validationFailed;
  }

  return null;
}

function mapCanonicalCategoryToFrontendKind(
  category: CanonicalErrorCategory
): FrontendErrorKind {
  switch (category) {
    case 'authentication':
    case 'authorization':
      return 'permission';
    case 'dependency_failure':
    case 'rate_limit':
      return 'dependency';
    case 'validation':
    case 'not_found':
    case 'conflict':
    case 'billing':
      return 'validation';
    case 'internal':
    default:
      return 'unexpected';
  }
}

export function classifyFrontendError(error: unknown): FrontendErrorKind {
  const metadata = extractErrorMetadata(error);
  const message = getErrorMessage(error);
  const canonical = resolveCanonicalDefinition(metadata, message);

  if (isTransientFetchError(error) || isNetworkMessage(message)) {
    return canonical
      ? mapCanonicalCategoryToFrontendKind(canonical.category)
      : 'network';
  }

  if (canonical) {
    return mapCanonicalCategoryToFrontendKind(canonical.category);
  }

  return 'unexpected';
}

function buildScopedUnexpectedDescription(sectionLabel?: string): string {
  if (!sectionLabel) {
    return userFacingMessageCatalog.errors.unexpected;
  }

  return `We hit an unexpected problem while loading ${sectionLabel.toLowerCase()}.`;
}

function buildScopedNetworkDescription(sectionLabel?: string): string {
  if (!sectionLabel) {
    return userFacingMessageCatalog.errors.network;
  }

  return `We couldn't reach Eclipse while loading ${sectionLabel.toLowerCase()}. Check your connection and try again.`;
}

function buildScopedDependencyDescription(sectionLabel?: string): string {
  if (!sectionLabel) {
    return userFacingMessageCatalog.errors.serviceUnavailable;
  }

  return `A service Eclipse depends on is having trouble while loading ${sectionLabel.toLowerCase()}. Try again in a few minutes.`;
}

export function getErrorStateContent(
  error: unknown,
  options: {
    sectionLabel?: string;
  } = {}
): ErrorStateContent {
  const latestContext = getLatestRequestContext();
  const metadata = extractErrorMetadata(error);
  const mergedRequestId = metadata.requestId || latestContext?.requestId;
  const message = getErrorMessage(error);
  const canonical = resolveCanonicalDefinition(metadata, message);
  const kind = classifyFrontendError(error);
  const userFacingCopy = getUserFacingErrorCopy(error);
  const authRequired =
    canonical?.category === 'authentication' ||
    metadata.status === 401 ||
    metadata.code === 'AUTH_REQUIRED' ||
    metadata.code === 'AUTH_ERROR';

  if (metadata.code === 'AUTH_UNAVAILABLE') {
    const authCopy = userFacingMessageCatalog.states.authUnavailable;
    return {
      authRequired: false,
      canRetry: true,
      description: authCopy.description,
      kind: 'dependency',
      nextAction: 'Retry sign-in in a moment, or go back home.',
      requestId: mergedRequestId,
      retryLabel: 'Try Again',
      title: authCopy.title,
    };
  }

  switch (kind) {
    case 'validation':
      if (canonical?.category === 'conflict') {
        return {
          authRequired: false,
          canRetry: true,
          description: userFacingCopy.message,
          kind,
          nextAction:
            'Refresh the page to pull the latest state, then try again.',
          requestId: mergedRequestId,
          retryLabel: 'Try Again',
          title: canonical.title,
        };
      }

      if (canonical?.category === 'not_found') {
        return {
          authRequired: false,
          canRetry: false,
          description: userFacingCopy.message,
          kind,
          nextAction:
            'Go back, reopen the record from the latest list, and try again.',
          requestId: mergedRequestId,
          retryLabel: 'Try Again',
          title: canonical.title,
        };
      }

      if (canonical?.category === 'billing') {
        return {
          authRequired: false,
          canRetry: false,
          description: userFacingCopy.message,
          kind,
          nextAction:
            'Review billing details for this workspace, then try again.',
          requestId: mergedRequestId,
          retryLabel: 'Try Again',
          title: canonical.title,
        };
      }

      return {
        authRequired: false,
        canRetry: false,
        description:
          userFacingCopy.kind === 'validation'
            ? userFacingCopy.message
            : userFacingMessageCatalog.errors.validation,
        kind,
        nextAction:
          'Go back, review the information that led here, and try again.',
        requestId: mergedRequestId,
        retryLabel: 'Try Again',
        title: 'Review the details',
      };
    case 'network':
      return {
        authRequired: false,
        canRetry: true,
        description: buildScopedNetworkDescription(options.sectionLabel),
        kind,
        nextAction: 'Check your connection, then try the page again.',
        requestId: mergedRequestId,
        retryLabel: 'Try Again',
        title: 'Check your connection',
      };
    case 'permission':
      return {
        authRequired,
        canRetry: false,
        description: authRequired
          ? userFacingMessageCatalog.errors.auth
          : userFacingMessageCatalog.errors.permission,
        kind,
        nextAction: authRequired
          ? 'Sign in again to reload this page.'
          : 'Switch organizations or ask an admin for access.',
        requestId: mergedRequestId,
        retryLabel: 'Try Again',
        title: authRequired ? 'Sign in again' : "You don't have access",
      };
    case 'dependency': {
      const isRateLimited =
        canonical?.category === 'rate_limit' || metadata.status === 429;

      return {
        authRequired: false,
        canRetry: true,
        description: isRateLimited
          ? userFacingMessageCatalog.errors.rateLimit
          : buildScopedDependencyDescription(options.sectionLabel),
        kind,
        nextAction: isRateLimited
          ? 'Wait a moment, then try again.'
          : 'Wait a minute, then try again.',
        requestId: mergedRequestId,
        retryLabel: 'Try Again',
        title: isRateLimited ? 'Try again soon' : 'Service is down',
      };
    }
    case 'unexpected':
    default:
      return {
        authRequired: false,
        canRetry: true,
        description: buildScopedUnexpectedDescription(options.sectionLabel),
        kind: 'unexpected',
        nextAction:
          'Try the page again. If it keeps failing, contact support with the reference below.',
        requestId: mergedRequestId,
        retryLabel: 'Try Again',
        supportRef: resolveSupportRef(metadata, latestContext),
        title: 'Reload this page',
      };
  }
}
